import { useEffect, useMemo, useRef } from 'react';

import {
  AddressDetailsDto,
  getEnecoBeXapiSiteApiV1OrdersByOrderId,
  getEnecoBeXapiSiteApiV1OrdersByOrderIdPreswitchBasicRecords,
  PreswitchBasicRecordDto,
} from '@eneco/dc-be/client';
import usePublicDCBE from '@eneco/dc-be/hooks/usePublicDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';

import { useOrderId } from './useOrderId';

const doesRecordMatchAddress = (record: PreswitchBasicRecordDto, deliveryAddress: AddressDetailsDto): boolean => {
  return record.addresses.some(psbAddress => {
    const houseNumberWithBus = `${deliveryAddress.houseNumber}${deliveryAddress.bus ? ` ${deliveryAddress.bus}` : ''}`;

    return (
      psbAddress.street.toLowerCase() === deliveryAddress.street.toLowerCase() &&
      psbAddress.postcode === deliveryAddress.postalCode &&
      psbAddress.city.toLowerCase() === deliveryAddress.municipality.toLowerCase() &&
      psbAddress.buildingNumber === houseNumberWithBus
    );
  });
};
export const usePreswitchBasicRecords = (): {
  preswitchBasicRecords: PreswitchBasicRecordDto[] | undefined | null;
  matchedElectricityRecords: PreswitchBasicRecordDto[];
  matchedGasRecords: PreswitchBasicRecordDto[];
  isLoading: boolean;
  error: unknown;
} => {
  const orderId = useOrderId();

  const { data: orderDataUnwrapped, isLoading: orderDataIsLoading } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1OrdersByOrderId,
    {
      path: {
        orderId: orderId ?? '',
      },
    },
    [`/orders/${orderId}`],
    !!orderId,
  );

  const preswitchBasicRecordsAbortControllerRef = useRef(new AbortController());
  const preswitchBasicRecordsSignal = preswitchBasicRecordsAbortControllerRef.current.signal;

  // cancel request after 5 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      preswitchBasicRecordsAbortControllerRef.current.abort();
    }, 5000);
    return () => clearTimeout(timeout);
  }, []);

  const {
    data: preswitchBasicDataUnwrapped,
    isLoading: preswitchBasicDataIsLoading,
    error: preswitchBasicDataError,
  } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1OrdersByOrderIdPreswitchBasicRecords,
    {
      path: {
        orderId: orderId ?? '',
      },
    },
    [`/orders/${orderId}/preswitch-basic-records`],
    !!orderId,
    preswitchBasicRecordsSignal,
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const orderData = unwrapData(orderDataUnwrapped);
  const preswitchBasicData = unwrapData(preswitchBasicDataUnwrapped);
  const deliveryAddress = orderData?.deliveryAddress;

  const matchedElectricityRecords = useMemo(() => {
    if (!Array.isArray(preswitchBasicData) || !deliveryAddress) {
      return [];
    }

    const electricityRecords = preswitchBasicData.filter(
      (record: PreswitchBasicRecordDto) => record.utilityType === 0 && doesRecordMatchAddress(record, deliveryAddress),
    );
    return electricityRecords;
  }, [deliveryAddress, preswitchBasicData]);

  const matchedGasRecords = useMemo(() => {
    if (!Array.isArray(preswitchBasicData) || !deliveryAddress) {
      return [];
    }

    return preswitchBasicData.filter(
      (record: PreswitchBasicRecordDto) => record.utilityType === 1 && doesRecordMatchAddress(record, deliveryAddress),
    );
  }, [deliveryAddress, preswitchBasicData]);

  return {
    preswitchBasicRecords: preswitchBasicData,
    matchedElectricityRecords,
    matchedGasRecords,
    isLoading: orderDataIsLoading || (preswitchBasicDataIsLoading && !preswitchBasicDataError),
    error: preswitchBasicDataError,
  };
};
